<template>
    <v-card>
        <v-form
            ref="identityFindAddForm"
            v-model="isValidIdentitySelectionForm"
        >
            <v-card-text>
                <v-container
                    class="pb-0"
                    fluid
                >
                    <v-row class="pb-0">
                        <v-col cols="12">
                            <v-select
                                :disabled="isLoading"
                                :items="identityTypes"
                                :rules="[requiredRule]"
                                @input="$refs.identityFindAddForm.validate()"
                                item-text="value"
                                item-value="key"
                                label="Identity Type"
                                v-model="type"
                            ></v-select>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                :disabled="isLoading"
                                :rules="[validRSAID,requiredRule,lengthRule(20)]"
                                label="Identification Number"
                                ref="idNumber"
                                v-model="number"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row
                        align="end"
                        class="pt-0"
                        justify="space-between"
                        no-gutters
                    >
                        <v-btn
                            :disabled="isLoading"
                            @click="closeForm"
                            class="text-none"
                            color="secondary darken-1"
                            text
                        >Close</v-btn>
                        <v-btn
                            :disabled="isLoading && !isLocating"
                            :loading="isLocating"
                            @click="locateIdentity"
                            class="text-none"
                            color="secondary darken-1"
                            text
                        >Next</v-btn>
                    </v-row>
                </v-container>
            </v-card-text>
        </v-form>
        <v-snackbar
            :color="notifyColor"
            :timeout="15000"
            top
            v-model="notifyShow"
        >
            {{ notifyMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    @click="notifyShow = false"
                    class="text-none"
                    icon
                    v-bind="attrs"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <v-dialog
            persistent
            v-model="patientSelectionFormVisible"
            width="500px"
        >
            <v-card>
                <v-form
                    :key="patientSelectionFormKey"
                    ref="patientSelectionForm"
                    v-if="identity"
                    v-model="isValidPatientSelectionForm"
                >
                    <v-card-text>
                        <v-container
                            class="pb-0"
                            fluid
                        >
                            <v-row class="pb-0">
                                <v-col cols="12">
                                    <div>
                                        {{identity.name}} {{identity.surname}}
                                        <br />
                                        {{identityTypes.find(x => x.key == identity.identityTypeId).value}}: {{identity.identificationNumber}}
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-autocomplete
                                        :disabled="isLoading"
                                        :items="incidentsWithPatient"
                                        item-text="'#'+id"
                                        item-value="patients[0]"
                                        label="Select Incident"
                                        v-model="patient"
                                        :rules="[requiredRule]"
                                    >
                                        <template v-slot:selection="data">
                                            Incident #{{data.item.id}}
                                        </template>
                                        <template v-slot:item="data">
                                            <template>
                                                <v-list-item-content>
                                                    <v-list-item-title>Incident #{{data.item.id}}</v-list-item-title>
                                                    <v-list-item-subtitle>Date: {{data.item.dateOf}}</v-list-item-subtitle>
                                                    <v-list-item-subtitle>ARN: {{data.item.accidentReportNumber}}</v-list-item-subtitle>                                                    
                                                </v-list-item-content>
                                            </template>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                            <v-row
                                align="end"
                                class="pt-0"
                                justify="space-between"
                                no-gutters
                            >
                                <v-btn
                                    :disabled="isLoading"
                                    @click="closePatientSelectionForm"
                                    class="text-none"
                                    color="secondary darken-1"
                                    text
                                >Back</v-btn>
                                <v-btn
                                    :disabled="isLoading"
                                    @click="newPatientLocated"
                                    class="text-none"
                                    color="secondary darken-1"
                                    text
                                >Select</v-btn>
                            </v-row>
                        </v-container>
                    </v-card-text>
                </v-form>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import uuid4 from "../../helpers/uuid4";
export default {
    props: [],
    //
    components: {
        //
    },
    mounted: async function () {
        this.identityTypes = await this.$store.dispatch("identities/getTypes");
    },
    data() {
        return {
            identityTypes: null,
            identity: null,
            isValidIdentitySelectionForm: null,
            isValidPatientSelectionForm: null,
            isLoading: null,
            isLocating: null,
            type: null,
            number: null,
            requestId: null,
            patient: null,

            patientSelectionFormVisible: false,
            patientSelectionFormKey: null,
            incidentsWithPatient: [],

            notifyShow: null,
            notifyMessage: null,
            notifyColor: null,

            requiredRule: (v) => !!v || "required",
            validRSAID: (v) => {
                if (this.type != 1 && this.type != 4) return true;

                var invalidId = "invalid RSA Id";
                if (!v || v.length != 13) return invalidId;

                var idNumbers;
                try {
                    idNumbers = v.split("").map((x) => parseInt(x));
                } catch {
                    return invalidId;
                }

                if (idNumbers[10] > 1) return invalidId;

                var evens = [
                    idNumbers[1],
                    idNumbers[3],
                    idNumbers[5],
                    idNumbers[7],
                    idNumbers[9],
                    idNumbers[11],
                ];

                var odds = [
                    idNumbers[0],
                    idNumbers[2],
                    idNumbers[4],
                    idNumbers[6],
                    idNumbers[8],
                    idNumbers[10],
                    idNumbers[12],
                ];

                var evensLuhnSum = evens
                    .map((x) => {
                        var d = 2 * x;
                        if (d > 9) {
                            var dstring = d.toString();
                            d = parseInt(dstring[0]) + parseInt(dstring[1]);
                        }
                        return d;
                    })
                    .reduce((a, b) => a + b, 0);

                var oddsSum = odds.reduce((a, b) => a + b, 0);

                return (evensLuhnSum + oddsSum) % 10 == 0 || invalidId;
            },
            lengthRule: (limit) => (v) =>
                !v || v.length <= limit || "exceeds " + limit + " characters",
        };
    },

    methods: {
        //emitters      ------------------------------------------------------------
        emitLocatedPatient(locatedPatient) {
            this.$emit("located", locatedPatient);
            this.closeForm();
        },
        emitCloseForm() {
            this.$emit("close");
        },
        //general       ------------------------------------------------------------
        validateIdentityForm() {
            this.isValidIdentitySelectionForm = this.$refs.identityFindAddForm.validate();
            return this.isValidIdentitySelectionForm;
        },
        validatePatientSelectionForm() {
            this.isValidPatientSelectionForm = this.$refs.patientSelectionForm.validate();
            return this.isValidPatientSelectionForm;
        },
        notify(message, color) {
            this.notifyShow = true;
            this.notifyMessage = message;
            this.notifyColor = color;
        },
        //new identity located ------------------------------------------
        newPatientLocated() {
            if (this.validatePatientSelectionForm()) {
                this.emitLocatedPatient(this.patient);
            }
        },
        //locate      ------------------------------------------------------------
        locateIdentity() {
            if (this.validateIdentityForm()) {
                this.isLoading = true;
                this.isLocating = true;

                var requestId = (this.requestId = uuid4());
                this.requestId = requestId;
                this.$store
                    .dispatch("identities/getByIdentificationTypeAndNumber", {
                        id: null,
                        urlParameters: {
                            type: this.type,
                            number: this.number,
                        },
                        requestId: requestId,
                    })
                    .then((identitiesResponse) => {
                        if (identitiesResponse.requestId == this.requestId) {
                            if (identitiesResponse.data) {
                                this.identity = identitiesResponse.data;
                                this.$store
                                    .dispatch(
                                        "incidents/getIncidentPatient",
                                        this.identity.id
                                    )
                                    .then((iResponse) => {
                                        this.incidentsWithPatient = iResponse;
                                        this.patientSelectionFormVisible = true;
                                    })
                                    .finally(this.locateIdentityDone);

                                //this.emitLocatedPatient(
                                //    identitiesResponse.data
                                //);
                            } else {
                                this.notify(
                                    "No patients were found with this identity.",
                                    "warning"
                                );
                                this.locateIdentityDone();
                            }
                        }
                    });
            }
        },
        locateIdentityDone() {
            this.isLoading = false;
            this.isLocating = false;
        },
        closeForm() {
            this.emitCloseForm();
        },
        closePatientSelectionForm() {
            this.patientSelectionFormVisible = false;
            this.patientSelectionFormKey = null;
            this.incidentsWithPatient = [];
        },
    },
};
</script>

<style lang="stylus" scoped></style>